import { useMemo, type ReactNode } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
  Archive,
  Download,
  LayoutGrid,
  Pause,
  Play,
  RefreshCw,
  Wand2,
  X,
} from 'lucide-react';
import { ACCENT } from '../../styles/tokens';
import { useBulkStore, type BulkSection } from '../../stores/bulkStore';
import { useSelectedIdStore } from '../../stores/selectedIdStore';
import { useEscalarModal } from '../modals/EscalarModalProvider';
import { toast } from '../../lib/toast';
import type { EscalarItem } from '../modals/EscalarModal';

const MOSAIC_MAX_TILES = 64;

export interface BulkActionBarProps {
  section: BulkSection;
  cluster: string;
  itemsResolver?: (ids: string[]) => EscalarItem[];
}

interface ActionDef {
  id: string;
  label: string;
  icon: ReactNode;
  primary?: boolean;
  danger?: boolean;
  onRun: (ctx: {
    ids: string[];
    cluster: string;
    items: EscalarItem[];
    taskIds: string[];
    navigate: NavigateFunction;
    openEscalar: ReturnType<typeof useEscalarModal>['openEscalar'];
    clear: () => void;
  }) => void;
}

const COMING_SOON = (label: string) => () => {
  toast(`${label}: em breve`, { tone: 'info' });
};

const ACTIONS: Record<BulkSection, ActionDef[]> = {
  queues: [
    {
      id: 'pause',
      label: 'Pausar',
      icon: <Pause size={11} />,
      onRun: () => COMING_SOON('Pausar')(),
    },
    {
      id: 'resume',
      label: 'Despausar',
      icon: <Play size={11} />,
      onRun: () => COMING_SOON('Despausar')(),
    },
    {
      id: 'restart',
      label: 'Reiniciar',
      icon: <RefreshCw size={11} />,
      onRun: () => COMING_SOON('Reiniciar')(),
    },
    {
      id: 'escalar',
      label: 'Escalar',
      icon: <Wand2 size={11} />,
      primary: true,
      onRun: ({ cluster, items, openEscalar, clear }) => {
        openEscalar({ type: 'bulk', cluster, items });
        clear();
      },
    },
  ],
  services: [
    {
      id: 'escalar',
      label: 'Escalar',
      icon: <Wand2 size={11} />,
      primary: true,
      onRun: ({ cluster, items, openEscalar, clear }) => {
        openEscalar({ type: 'bulk', cluster, items });
        clear();
      },
    },
    {
      id: 'mosaic',
      label: 'Ver no Mosaic',
      icon: <LayoutGrid size={11} />,
      onRun: ({ taskIds, navigate, clear }) => {
        if (taskIds.length === 0) {
          toast('Nenhuma réplica com VNC nos serviços selecionados', {
            tone: 'info',
          });
          return;
        }
        const selected = taskIds.slice(0, MOSAIC_MAX_TILES).join(',');
        navigate(`/mosaic?selected=${encodeURIComponent(selected)}`);
        clear();
      },
    },
  ],
  jobs: [
    {
      id: 'retry',
      label: 'Reprocessar',
      icon: <RefreshCw size={11} />,
      primary: true,
      onRun: () => COMING_SOON('Reprocessar')(),
    },
    {
      id: 'archive',
      label: 'Arquivar',
      icon: <Download size={11} />,
      onRun: () => COMING_SOON('Arquivar')(),
    },
    {
      id: 'discard',
      label: 'Descartar',
      icon: <Archive size={11} />,
      danger: true,
      onRun: () => COMING_SOON('Descartar')(),
    },
  ],
  nodes: [],
};

export function BulkActionBar({
  section,
  cluster,
  itemsResolver,
}: BulkActionBarProps) {
  const selection = useBulkStore(s => s.selections[section]);
  const clear = useBulkStore(s => s.clear);
  const storeResolver = useSelectedIdStore(s => s.resolvers[section]);
  const taskResolver = useSelectedIdStore(s => s.taskResolvers[section]);
  const navigate = useNavigate();
  const { openEscalar } = useEscalarModal();

  const ids = useMemo(() => Array.from(selection), [selection]);
  const count = ids.length;
  const actions = ACTIONS[section] ?? [];

  if (count === 0 || actions.length === 0) return null;

  const resolver = itemsResolver ?? storeResolver;
  const items: EscalarItem[] = resolver
    ? resolver(ids)
    : ids.map(id => ({ id, name: id, currentReplicas: 0 }));
  const taskIds = taskResolver ? taskResolver(ids) : [];

  return (
    <div
      data-testid="bulk-action-bar"
      role="toolbar"
      aria-label={`Ações em massa (${section})`}
      className="fixed bottom-3.5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2.5 rounded-[10px] bg-primary py-2 pl-3.5 pr-2 text-white shadow-[0_12px_32px_rgba(15,23,42,.18),0_0_0_1px_rgba(255,255,255,.05)_inset] font-sans"
    >
      <span className="font-mono text-[12px] font-bold">
        <span style={{ color: ACCENT }}>{count}</span> selecionado
        {count !== 1 ? 's' : ''}
      </span>

      <span aria-hidden className="h-5 w-px bg-white/15" />

      <div className="flex gap-1">
        {actions.map(action => {
          const baseClass =
            'inline-flex h-7 items-center gap-1.5 rounded-md border-none px-2.5 font-sans text-[11.5px] font-semibold text-white transition-colors';
          const variant = action.primary
            ? 'hover:opacity-90'
            : action.danger
              ? 'bg-[rgba(239,68,68,.2)] hover:bg-[rgba(239,68,68,.3)]'
              : 'bg-white/[.08] hover:bg-white/[.14]';
          const style = action.primary ? { background: ACCENT } : undefined;
          return (
            <button
              key={action.id}
              type="button"
              data-testid={`bulk-action-${action.id}`}
              onClick={() =>
                action.onRun({
                  ids,
                  cluster,
                  items,
                  taskIds,
                  navigate,
                  openEscalar,
                  clear: () => clear(section),
                })
              }
              className={`${baseClass} ${variant}`}
              style={style}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          );
        })}
      </div>

      <span aria-hidden className="h-5 w-px bg-white/15" />

      <button
        type="button"
        data-testid="bulk-action-clear"
        aria-label="Limpar seleção"
        onClick={() => clear(section)}
        className="flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-white/60 transition-colors hover:bg-white/[.08] hover:text-white"
      >
        <X size={12} />
      </button>
    </div>
  );
}

export default BulkActionBar;
